import { Fragment, Key } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ICommentAPI } from '../index';
import { Typography } from '../../../molecules/Typography';
import { Button } from '../../../atoms/Button';
import NamePill from '../../../molecules/NamePill';
import CommentCard from './comment/indext';
import InputDiv from './inputDiv';
import BrainLoadingWrapper from '../../../molecules/BrainLoading';

interface ICommentsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  comments: ICommentAPI[];
  totalComments: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  enableShowAll: boolean;
  onLike: (id: string) => void;
  onReply: (text: string, id: string) => void;
  onNewComment: (text: string) => void;
  onDelete: (comment: any) => void;
  onUpdate: (comment: any, newText: string) => void;
  deleteLoading: boolean;
  fetchMoreComments: () => void;
}

export default function CommentsDrawer({
  isOpen,
  onClose,
  comments,
  totalComments,
  isLoading,
  isLoadingMore,
  enableShowAll,
  onLike,
  onReply,
  onNewComment,
  onDelete,
  onUpdate,
  deleteLoading,
  fetchMoreComments,
}: ICommentsDrawerProps) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as='div' className='relative z-50' onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-mono-color-black/70' />
        </Transition.Child>

        <div className='fixed inset-0 flex items-end'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='translate-y-full'
            enterTo='translate-y-0'
            leave='ease-in duration-200'
            leaveFrom='translate-y-0'
            leaveTo='translate-y-full'
          >
            <Dialog.Panel
              className='rounded-t-2xl flex w-full flex-col bg-color-lightest'
              style={{ maxHeight: '85vh' }}
            >
              <div className='border-b flex items-center justify-between border-mono-color-lighter px-4 py-3'>
                <Typography type='title' size='h4'>
                  {totalComments} comentários
                </Typography>
                <Button
                  onlyIcon
                  LeftIcon='close'
                  iconType='material'
                  variant='text'
                  onClick={onClose}
                />
              </div>

              <div className='flex-1 overflow-y-auto px-4 py-4'>
                {isLoading ? (
                  <BrainLoadingWrapper />
                ) : (
                  <div className='flex min-w-0 flex-col gap-xl'>
                    {comments.map(
                      (comment: ICommentAPI, i: Key | null | undefined) => (
                        <div className='flex min-w-0 flex-row gap-3' key={i}>
                          <div className='w-8 shrink-0'>
                            <NamePill
                              profile={comment.user.picture}
                              name={comment.user.name}
                              color='primary'
                              size='tiny'
                            />
                          </div>
                          <div className='flex min-w-0 flex-1 flex-col gap-xl overflow-hidden'>
                            <CommentCard
                              comment={comment}
                              disableReply={!comment._id}
                              onSubmit={(text: string) =>
                                onReply(text, comment._id)
                              }
                              onLike={() => onLike(comment._id)}
                              isModal={true}
                              onDelete={() => onDelete(comment)}
                              deleteLoading={deleteLoading}
                              onUpdate={(value: string) =>
                                onUpdate(comment, value)
                              }
                            />
                            {comment.children &&
                              comment.children.length > 0 &&
                              comment.children.map(
                                (reply: ICommentAPI, j: number) => (
                                  <div
                                    className='flex min-w-0 flex-row gap-3 pl-2'
                                    key={j}
                                  >
                                    <div className='w-8 shrink-0'>
                                      <NamePill
                                        profile={reply.user.picture}
                                        name={reply.user.name}
                                        color='primary'
                                      />
                                    </div>
                                    <div className='flex min-w-0 flex-1 overflow-hidden'>
                                      <CommentCard
                                        comment={reply}
                                        disableReply={true}
                                        onSubmit={() => {}}
                                        onLike={() => onLike(reply._id)}
                                        isModal={true}
                                        onDelete={() => onDelete(reply)}
                                        deleteLoading={deleteLoading}
                                        onUpdate={(value: string) =>
                                          onUpdate(reply, value)
                                        }
                                      />
                                    </div>
                                  </div>
                                )
                              )}
                          </div>
                        </div>
                      )
                    )}

                    {enableShowAll && (
                      <div className='flex justify-center'>
                        <Button
                          variant='text'
                          size='regular'
                          LeftIcon='expand_more'
                          loading={isLoadingMore}
                          onClick={() => fetchMoreComments()}
                        >
                          Ver mais comentários
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className='border-t border-mono-color-lighter px-4 py-3'>
                <InputDiv onSubmit={(text: string) => onNewComment(text)} />
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
